import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Cross, LogOut, Menu, X, Languages, Bell } from "lucide-react";
import { cn } from "@/lib/utils";
import { API, api } from "@/lib/api";

function LangToggle({ dark }) {
  const { lang, setLang } = useLang();
  return (
    <button
      onClick={() => setLang(lang === "fr" ? "en" : "fr")}
      data-testid="lang-toggle"
      className={cn(
        "flex items-center gap-1.5 px-2.5 h-9 rounded-md text-xs font-heading font-bold border transition-colors",
        dark ? "border-white/20 text-primary-foreground hover:bg-white/10" : "border-border text-foreground hover:bg-secondary"
      )}
    >
      <Languages className="h-4 w-4" />
      {lang === "fr" ? "FR" : "EN"}
    </button>
  );
}

function Brand({ subtitle }) {
  return (
    <div className="flex items-center gap-2.5">
      <div className="h-9 w-9 rounded-lg bg-white flex items-center justify-center shrink-0 overflow-hidden p-0.5">
        <img src={`${API}/settings/logo`} alt="ICDH" className="h-full w-full object-contain"
          onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.nextSibling.style.display = "flex"; }} />
        <div className="h-full w-full items-center justify-center bg-accent rounded" style={{ display: "none" }}>
          <Cross className="h-5 w-5 text-primary" />
        </div>
      </div>
      <div className="leading-tight">
        <p className="font-heading font-extrabold text-sm text-primary-foreground">ICDH School</p>
        <p className="text-[11px] text-primary-foreground/60">{subtitle}</p>
      </div>
    </div>
  );
}

function UserChip({ user, onLogout, dark }) {
  return (
    <div className="flex items-center gap-3">
      <Avatar className="h-9 w-9 border border-border">
        {user.picture && <AvatarImage src={user.picture} />}
        <AvatarFallback className="bg-accent text-primary font-heading font-bold text-xs">
          {(user.name || "?").slice(0, 2).toUpperCase()}
        </AvatarFallback>
      </Avatar>
      <div className="leading-tight hidden sm:block">
        <p className={cn("text-sm font-semibold", dark ? "text-primary-foreground" : "text-foreground")}>{user.name}</p>
        <p className={cn("text-[11px] capitalize", dark ? "text-primary-foreground/60" : "text-muted-foreground")}>{user.role === "admin" ? "Administration" : user.role === "staff" ? "Staff" : (user.role === "teacher" && user.admin_access) ? "Teacher · Admin" : user.role}</p>
      </div>
      <Button size="icon" variant="ghost" onClick={onLogout} title="Sign out"
        className={dark ? "text-primary-foreground hover:bg-white/10" : ""} data-testid="logout-button">
        <LogOut className="h-4 w-4" />
      </Button>
    </div>
  );
}

/* Notification bell with unread badge (announcements feed) */
export function NotificationBell() {
  const { L } = useLang();
  const [open, setOpen] = useState(false);
  const [count, setCount] = useState(0);
  const [items, setItems] = useState([]);
  const load = async () => { try { const r = await api.get("/announcements/unread-count"); setCount(r.unread || 0); } catch (e) { /* noop */ } };
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, []);
  const toggle = async () => {
    const willOpen = !open; setOpen(willOpen);
    if (willOpen) {
      try { const r = await api.get("/announcements"); setItems((r || []).slice(0, 20)); await api.post("/announcements/read-all"); setCount(0); } catch (e) { /* noop */ }
    }
  };
  return (
    <div className="relative">
      <button onClick={toggle} data-testid="notif-bell" title="Notifications"
        className="relative flex items-center justify-center h-9 w-9 rounded-md border border-border hover:bg-secondary transition-colors">
        <Bell className="h-4 w-4" />
        {count > 0 && <span data-testid="notif-badge" className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center">{count > 9 ? "9+" : count}</span>}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-2 w-80 max-h-96 overflow-y-auto rounded-lg border border-border bg-card shadow-xl z-40 p-2" data-testid="notif-panel">
            <p className="px-2 py-1.5 text-xs font-heading font-bold text-muted-foreground uppercase tracking-wide">Notifications</p>
            {items.length === 0
              ? <p className="px-2 py-6 text-sm text-muted-foreground text-center">{L("No notifications", "Aucune notification")}</p>
              : items.map((a) => (
                <div key={a.id} className="px-2 py-2 rounded-md hover:bg-secondary" data-testid={`notif-item-${a.id}`}>
                  <p className="text-sm font-semibold text-foreground">{a.title || "—"}</p>
                  <p className="text-xs text-muted-foreground whitespace-pre-wrap">{a.message}</p>
                  {a.created_at && <p className="text-[10px] text-muted-foreground mt-0.5">{new Date(a.created_at).toLocaleString()}</p>}
                </div>
              ))}
          </div>
        </>
      )}
    </div>
  );
}

/* Sidebar layout for admin/staff/teacher */
export function SidebarShell({ nav, active, onNav, title, children, headerExtra }) {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  return (
    <div className="min-h-screen bg-background flex">
      {/* Sidebar */}
      <aside className={cn(
        "fixed lg:sticky top-0 z-40 h-screen w-64 bg-primary flex flex-col transition-transform lg:translate-x-0",
        open ? "translate-x-0" : "-translate-x-full"
      )}>
        <div className="p-5 flex items-center justify-between border-b border-white/10">
          <Brand subtitle={user.role === "admin" ? "Administration" : user.role === "staff" ? "Staff" : (user.role === "teacher" && user.admin_access) ? "Teacher · Admin" : user.role === "teacher" ? "Teacher" : "Administration"} />
          <button className="lg:hidden text-primary-foreground" onClick={() => setOpen(false)}><X className="h-5 w-5" /></button>
        </div>
        <nav className="flex-1 overflow-y-auto p-3 space-y-1">
          {nav.map((item) => (
            <button
              key={item.key}
              onClick={() => { onNav(item.key); setOpen(false); }}
              data-testid={`nav-${item.key}`}
              className={cn(
                "w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors",
                active === item.key
                  ? "bg-accent text-primary font-semibold"
                  : "text-primary-foreground/70 hover:bg-white/10 hover:text-primary-foreground"
              )}
            >
              <item.icon className="h-[18px] w-[18px] shrink-0" />
              {item.label}
            </button>
          ))}
        </nav>
        <div className="p-4 border-t border-white/10">
          <UserChip user={user} onLogout={logout} dark />
        </div>
      </aside>

      {open && <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setOpen(false)} />}

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="sticky top-0 z-20 backdrop-blur-xl bg-background/70 border-b border-border px-5 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button className="lg:hidden" onClick={() => setOpen(true)}><Menu className="h-5 w-5" /></button>
            <h1 className="font-heading font-extrabold text-xl tracking-tight text-foreground">{title}</h1>
          </div>
          <div className="flex items-center gap-2">
            {headerExtra}
            <LangToggle />
          </div>
        </header>
        <main className="flex-1 p-5 sm:p-8 max-w-[1400px] w-full">{children}</main>
      </div>
    </div>
  );
}

/* Top nav layout for parents */
export function TopNavShell({ nav, active, onNav, children }) {
  const { user, logout } = useAuth();
  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header className="sticky top-0 z-20 backdrop-blur-xl bg-primary/95 border-b border-white/10">
        <div className="max-w-[1300px] mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Brand subtitle="Parent Portal" />
          <div className="flex items-center gap-2">
            <LangToggle dark />
            <UserChip user={user} onLogout={logout} dark />
          </div>
        </div>
        <div className="max-w-[1300px] mx-auto px-2 sm:px-6 grid grid-cols-4 gap-1 pb-2 sm:flex sm:flex-wrap sm:gap-1 sm:pb-0">
          {nav.map((item) => (
            <button
              key={item.key}
              onClick={() => onNav(item.key)}
              data-testid={`nav-${item.key}`}
              className={cn(
                "flex flex-col items-center justify-start gap-1 rounded-lg px-1 py-2 text-[10.5px] font-medium leading-tight text-center transition-colors",
                "sm:flex-row sm:justify-center sm:gap-2 sm:rounded-none sm:px-3 sm:py-2.5 sm:text-sm sm:leading-normal sm:border-b-2 sm:whitespace-nowrap",
                active === item.key
                  ? "bg-white/10 text-accent sm:bg-transparent sm:border-accent"
                  : "text-primary-foreground/70 hover:text-primary-foreground hover:bg-white/5 sm:border-transparent sm:hover:bg-transparent"
              )}
            >
              <item.icon className="h-5 w-5 shrink-0 sm:h-4 sm:w-4" />
              <span>{item.label}</span>
            </button>
          ))}
        </div>
      </header>
      <main className="flex-1 w-full max-w-[1300px] mx-auto p-4 sm:p-8">{children}</main>
    </div>
  );
}

export function StatCard({ label, value, icon: Icon, tone = "primary" }) {
  const tones = {
    primary: "text-primary",
    accent: "text-accent",
    danger: "text-destructive",
    muted: "text-muted-foreground",
  };
  return (
    <div className="bg-card border border-border rounded-lg p-5 transition-transform hover:-translate-y-1 hover:shadow-md" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center justify-between">
        <span className="text-sm text-muted-foreground font-medium">{label}</span>
        {Icon && <Icon className={cn("h-5 w-5", tones[tone])} />}
      </div>
      <p className="mt-3 font-heading font-extrabold text-3xl tracking-tight text-foreground">{value}</p>
    </div>
  );
}

export function PageHeader({ title, description, action }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
      <div>
        <h2 className="font-heading font-extrabold text-2xl tracking-tight text-foreground">{title}</h2>
        {description && <p className="text-muted-foreground mt-1 text-sm">{description}</p>}
      </div>
      {action}
    </div>
  );
}

export function Empty({ title, hint }) {
  return (
    <div className="border border-dashed border-border rounded-lg p-12 text-center bg-card" data-testid="empty-state">
      <p className="font-heading font-semibold text-foreground">{title}</p>
      {hint && <p className="text-muted-foreground text-sm mt-1">{hint}</p>}
    </div>
  );
}
